import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import useAppStore from "@/context/store";
import {
  getOrder,
  getSubscribeActivities,
  getSubscriptionList,
} from "@/services";
import { getAdaptiveImg } from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import { useControllableValue, useUpdateEffect } from "ahooks";
import { Tooltip } from "antd";
import classNames from "classnames";
import { flatten, without } from "lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlusActivityModule, usePlusActivity } from "../activity/plusActivity";
import PayBottom from "../pay/payBottom";
import { VipDialogContext } from "../shared/configContext";
import Swiper from "../shared/swiper";
import {
  getOrigrinPrice,
  getPayDetailBySkus,
  getPrice,
  getSkusByCode,
} from "../shared/utils";
import styles from "./index.module.less";
import LeftInfo from "./left";
import UpgradeTab from "./upgradeTab";

export default function CompanyTab({
  goods,
  defaultSelectedPkg,
  defaultSelectPlusSku,
  versionTab,
  onOrderDone,
  isExpVip,
  isUpgrade,
  onDisableChange,
  onTransfer,
}) {
  if (isExpVip || isUpgrade) {
    return (
      <UpgradeTab
        goods={goods}
        defaultSelectedPkg={defaultSelectedPkg}
        onOrderDone={onOrderDone}
        isUpgrade={isUpgrade}
        isExpVip={isExpVip}
        onDisableChange={onDisableChange}
        onTransfer={onTransfer}
      />
    );
  }
  return (
    <BuyCompanyTab
      goods={goods}
      defaultSelectedPkg={defaultSelectedPkg}
      defaultSelectPlusSku={defaultSelectPlusSku}
      versionTab={versionTab}
      onOrderDone={onOrderDone}
      onDisableChange={onDisableChange}
      onTransfer={onTransfer}
    />
  );
}

function BuyCompanyTab({
  goods,
  onOrderDone,
  onDisableChange,
  defaultSelectedPkg,
  defaultSelectPlusSku,
  versionTab,
  onTransfer,
}) {
  const { user, setUser } = useAppStore(({ setUser, user }) => ({
    setUser,
    user,
  }));
  const currentCardRef = useRef();
  const swiperScrollRef = useRef();

  const { config, rightData } = useContext(VipDialogContext);
  const { company: companyTabConf } = config;
  const subscription = rightData.subscription[0];

  const [currentVersionTab, setCurrentVersionTab] = useState(+versionTab || 0);

  const onVersionTabClick = useCallback((tabIndex) => {
    setCurrentVersionTab(tabIndex);
  }, []);

  const getOptions = useCallback(
    (card) => {
      return card.selectedPkgs.map((item) => {
        return {
          label: item.text,
          value: item.pkg ? item.pkg : null,
          skus: getSkusByCode(goods, item.pkg || ""),
        };
      });
    },
    [goods]
  );

  // 处理展示会员卡片列表信息，方便一些信息获取
  const cardList = useMemo(() => {
    const subscriptionSkuId = +subscription?.items[0].sku_id;

    let cards = companyTabConf?.versions[currentVersionTab]?.list;

    return (
      cards
        .map((card, index) => ({
          ...card,
          index,
          options: card.selectedPkgs ? getOptions(card) : null,
          skus: getSkusByCode(goods, card.goodCode),
        }))
        // 过滤没有匹配到商品的，避免支付出错
        .filter((card) => card.skus.length)
        // 过滤已订阅的商品，避免重复订阅
        .filter((card) => !card.skus.find((sku) => sku.id == subscriptionSkuId))
    );
  }, [companyTabConf, goods, getOptions, subscription, currentVersionTab]);

  const [disabled, setDisabled] = useState(false);
  const onDisableChangeWrap = useCallback(
    (state) => {
      setDisabled(state);
      onDisableChange && onDisableChange(state);
    },
    [onDisableChange]
  );

  // 续费场景|指定包情况下，默认选中当前用户生效会员的套餐
  const { loading: defaultLoading } = useRequest(async () => {
    // 当指定套餐时，默认选中指定套餐
    if (defaultSelectedPkg) {
      // 指定选中卡片
      const cardPkg = defaultSelectedPkg[0];
      const selectCard = cardList.find((c) => cardPkg === c.goodCode);

      // 指定可选套餐
      const selectPkgs = defaultSelectedPkg[1];

      if (selectCard) {
        setCurrent(selectCard);
        if (selectPkgs && selectCard?.options?.length > 1) {
          const findPkg = selectCard.options.find(
            (opt) => opt.value === selectPkgs
          );
          findPkg && setSelectedPkg([findPkg]);
        }
      } else {
        if (selectPkgs && current?.options?.length > 1) {
          setSelectedPkg([current.options[1]]);
        }
      }
      return;
    }

    if (rightData && rightData.vips.length) {
      if (rightData.vipZone.source_type === "exchange") {
        return;
      }

      // 获取当前生效会员对应订单，用于判断当前会员买的是哪个套餐
      const order = await getOrder(rightData.vipZone.order_no);

      // 从订单信息中获取是会员商品
      const vipGood = order.skus.find(
        (sku) => sku.ref_body.goods.type === "vip"
      ).ref_body.goods;

      // 根据会员商品code，默认选中对应会员卡片
      const current = cardList.find((c) => c.goodCode === vipGood.code);
      if (current) {
        setCurrent(current);
      }

      // 根据当前是否有升级包权益，来判断是否默认选中升级包
      if (
        rightData.upgrade_rights.length &&
        current?.options &&
        current?.options.length > 1
      ) {
        setSelectedPkg([current.options[1]]);
      }

      if (
        rightData.upgrade_plus_rights.length &&
        current.options &&
        current.options.length > 1
      ) {
        setSelectedPkg([current.options[2]]);
      }
    }
  });

  // 现在选中的参数
  const [current, setCurrent] = useState(
    cardList.find((card) => card.default) || cardList[0]
  );

  // 选中的升级包
  const [selectedPkg, setSelectedPkg] = useState();

  // 切换卡片时，重置升级包选择
  useEffect(() => {
    if (currentCardRef.current) {
      swiperScrollRef.current.scrollToTarget(currentCardRef.current);
    }
  }, [current]);

  // 切换版本时，重置卡片选择
  useUpdateEffect(() => {
    setCurrent(cardList.find((card) => card.default) || cardList[0]);
    setSelectedPkg();
    setSelectedPlusSku([]);
  }, [currentVersionTab, cardList]);

  // 根据配置中的activity获取活动Id，然后调用接口获取活动信息
  const { data: activities } = useRequest(async () => {
    let activityIds = [];

    for (let i = 0; i < cardList.length; i++) {
      const card = cardList[i];
      card?.activity && activityIds.push(card.activity);
    }
    if (activityIds.length) {
      let [activitieList, subscribeList] = await Promise.all([
        getSubscribeActivities(activityIds.join()).then((res) => {
          if (res.err) return [];
          return res;
        }),
        getSubscriptionList(),
      ]);

      const hasSubscribeSkuIds = subscribeList.list
        // 筛选创建中的订阅（支付扫码后没有确认订阅的）
        .filter((s) => s.status !== "created")
        // 获取对应的skuId
        .map((s) => +s.items[0].sku_id);

      const activityMap = {};

      activitieList
        // 筛选进行中的活动
        .filter((at) => at.activity.status === "STARTED")
        // 筛选掉已经参加过优惠的活动
        .filter((at) => {
          const activitySkuId = at.detail?.subscribe_goods[0]?.sku_id;
          return !hasSubscribeSkuIds.includes(activitySkuId);
        })
        // 遍历取出需要用到的活动信息
        .forEach((at) => {
          activityMap[at.activity.id] = {
            activityId: at.activity.id,
            remarks: at.activity.remarks,
            detail: at.detail,
            price: at.detail?.subscribe_goods[0]?.price,
          };
        });

      // 活动与卡片索引映射
      const cardActivityMap = {};
      for (let i = 0; i < cardList.length; i++) {
        const card = cardList[i];
        if (card.activity && activityMap[card.activity]) {
          const activity = activityMap[card.activity];
          const skuIds = activity?.detail?.subscribe_goods.map(
            (good) => good.sku_id
          );
          card.skus.forEach((sku) => {
            if (skuIds.includes(sku.id)) {
              cardActivityMap[card.goodCode] = activity;
            }
          });
        }
      }
      return cardActivityMap;
    }

    return [];
  });

  const { selectedPlusSku, setSelectedPlusSku, plusActivity } = usePlusActivity(
    {
      current,
      defaultSelectPlusSku,
      config: config?.upgradedPurchase,
    }
  );

  const payDetail = useMemo(() => {
    if (!activities) return null;
    if (defaultLoading) return null;
    if (!current) return null;

    // 获取卡片对应的商品明细
    const vipDetail = getPayDetailBySkus(current.skus, 1, {
      start: rightData.vip_expire,
      rightPkgCodeMap: rightData.rightPkgCodeMap,
    });

    // 明细加上活动信息
    if (activities && activities[current.goodCode]) {
      vipDetail[0].activity = activities[current.goodCode];
    }

    const details = [...vipDetail];

    if (selectedPkg && selectedPkg[0].value) {
      getPayDetailBySkus(selectedPkg[0].skus, 1, {
        rightPkgCodeMap: rightData.rightPkgCodeMap,
      }).forEach((detail) => {
        details.push(detail);
      });
    }

    // 换购商品
    selectedPlusSku.forEach((item) => {
      details.push({
        code: item.sku.goods.code,
        name: item.sku.name,
        skuId: item.sku.id,
        // 这里原价
        price: item.sku.price,
        number: 1,
        good: item.sku.goods,
        type: "plusSku",
        // 主商品skuId
        mainSkuId: current.skus[0].id,
        activity: item?.activity,
      });
    });

    const plusDiscountPrice = selectedPlusSku.reduce((pre, item) => {
      return pre + item.price - item.sku.price;
    }, 0);

    // 优惠价格信息
    if (plusDiscountPrice) {
      details.push({
        type: "discount",
        name: "换购活动优惠",
        price: plusDiscountPrice,
      });
    }

    return details;
  }, [
    activities,
    defaultLoading,
    current,
    rightData,
    selectedPkg,
    selectedPlusSku,
  ]);

  const onOrderDoneWrap = useCallback(
    (order) => {
      onOrderDone && onOrderDone(order);
      // 更新用户正确信息
      setUser({
        ...user,
        extra: {
          ...(user.extra || {}),
          material_vip: 20,
          material_vip_expire_at: payDetail[0].expire,
        },
      });
    },
    [setUser, onOrderDone, payDetail, user]
  );

  const onCardClick = useCallback(
    (item) => {
      if (current === item) {
        return;
      }
      !disabled && setCurrent(item);
      setSelectedPkg();
      setSelectedPlusSku([]);
    },
    [current, disabled, setSelectedPlusSku]
  );

  const selectedPkgPrice = useMemo(() => {
    let price = 0;
    let origin = 0;

    if (selectedPkg && selectedPkg.length) {
      for (let i = 0; i < selectedPkg.length; i++) {
        const pkg = selectedPkg[i];
        const skuPrice = getPrice(pkg.skus);
        price += skuPrice;
        origin += getOrigrinPrice(pkg.skus) || skuPrice;
      }
    }

    if (origin <= price) {
      origin = 0;
    }

    return {
      price,
      origin,
    };
  }, [selectedPkg]);

  // 自适应卡片宽度，小于等于3张时，自适应铺满宽度，最大是一半宽度，大于3张时，固定宽度
  const cardWidth = useMemo(() => {
    if (cardList?.length) {
      const computedWidth =
        cardList?.length <= 3
          ? (689 - 12 * (cardList.length - 1)) / cardList?.length
          : 218;
      return Math.min(computedWidth, 333);
    }

    return 218; // 默认宽度
  }, [cardList?.length]);

  return (
    <>
      <LeftInfo
        config={config.leftInfo?.find((item) => item.versionType === "company")}
      />
      <div
        className={styles.right}
        data-order-type={"会员" + (user?.extra?.material_vip ? "" : "_首购")}
        data-good-second-type="企业VIP"
      >
        <div className={classNames(styles.versionTab, styles.companyVersion)}>
          {companyTabConf?.versions.map((item, index) => (
            <div
              key={index}
              className={classNames(styles.versionTabItem, {
                [styles.selected]: currentVersionTab === index,
              })}
              onClick={() => onVersionTabClick(index)}
            >
              <span className={styles.name}>
                {item.name}
                <Tooltip
                  title={item.tip}
                  placement="bottomLeft"
                  align={{ offset: [-12, 4] }}
                >
                  <HBIcon type="ic_info" />
                </Tooltip>
              </span>
              <span dangerouslySetInnerHTML={{ __html: item.describe }}></span>
            </div>
          ))}
        </div>
        <Swiper
          className={styles.companyTab}
          width={708}
          calibration={-34}
          ref={swiperScrollRef}
        >
          {cardList.map((item, index) => (
            <div
              key={index}
              className={classNames(styles.card, {
                [styles.selected]: current === item,
                [styles.disabled]: disabled,
              })}
              onClick={() => {
                onCardClick(item);
              }}
              {...(current === item
                ? {
                    ref: currentCardRef,
                  }
                : {})}
              style={
                item.cardBackImg && item.cardBackImg.length
                  ? {
                      backgroundImage: `url(${getAdaptiveImg(
                        item.cardBackImg[0].url,
                        {
                          width: cardWidth,
                        }
                      )})`,
                    }
                  : {}
              }
            >
              {/* 营销角标显示 */}
              {item.tag && item.tag.length > 0 && (
                <div className={styles.tag}>
                  <Image
                    key={item.tag[0]?.url}
                    alt={"活动"}
                    height={26}
                    src={item.tag[0]?.url}
                    loading="eager"
                  />
                </div>
              )}
              <div
                className={styles.cardWrap}
                style={
                  item.cardBackImg && item.cardBackImg.length
                    ? {
                        background: "transparent",
                        borderColor: "transparent",
                        width: cardWidth,
                      }
                    : {
                        width: cardWidth,
                      }
                }
              >
                {/* 商品标题 */}
                <div className={styles.title}>{item.title}</div>
                {/* 商品价格 */}
                <div className={styles.price}>
                  <span>
                    ¥
                    <span>
                      {getPrice(
                        item.skus,
                        activities && activities[item.goodCode]
                      ) + (current === item ? selectedPkgPrice.price : 0)}
                    </span>
                    <span className={styles.afterPrice}>
                      {current !== item && item.afterPrice}
                    </span>
                    {item.unit}
                  </span>
                  {/* 划线价 */}
                  <span className={styles.origin}>
                    {getOrigrinPrice(
                      item.skus,
                      activities && activities[item.goodCode],
                      "¥",
                      current === item
                        ? selectedPkgPrice.origin || selectedPkgPrice.price
                        : 0
                    )}
                  </span>
                </div>
                {/* 商品基本描述（卖点），活动描述优先 */}
                <div className={styles.info}>
                  {(activities && activities[item.goodCode]?.remarks) ||
                    item.desc}
                </div>
                {/* 商品详细描述列表 */}
                <ul className={styles.desc}>
                  <li>
                    {current === item && item.selectedPkgs?.length > 0 ? (
                      <>
                        <span>每天下载</span>
                        <CheckGroup
                          value={selectedPkg}
                          options={item.options}
                          onChange={setSelectedPkg}
                          itemWidth={37}
                          disabled={disabled}
                        />
                      </>
                    ) : (
                      <span
                        dangerouslySetInnerHTML={{ __html: item.downloadInfo }}
                      ></span>
                    )}
                  </li>
                  {item.authInfo && (
                    <li style={{ marginBottom: -6 }}>
                      <span
                        dangerouslySetInnerHTML={{ __html: item.authInfo }}
                      ></span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          ))}
        </Swiper>

        {plusActivity?.skus?.length > 0 && (
          <PlusActivityModule
            isCompany
            goods={plusActivity?.skus || []}
            selected={selectedPlusSku}
            onSelect={setSelectedPlusSku}
            config={config.upgradedPurchase}
          />
        )}

        <PayBottom
          // scene={scene}
          // tipTxt={tip}
          theme={"company"}
          hasSubscribeTip
          details={payDetail}
          onOrderDone={onOrderDoneWrap}
          style={{ margin: "24px 30px 0 0" }}
          onDisableChange={onDisableChangeWrap}
          onTransfer={onTransfer}
          protocols={flatten(
            selectedPlusSku.map((item) => item.config.protocols)
          )}
        />
      </div>
    </>
  );
}

function CheckItem({ children, checked, onChange, width, disabled }) {
  return (
    <div
      className={classNames(styles.checkBox, {
        [styles.checked]: checked,
        [styles.disabled]: disabled,
      })}
      onClick={() => !disabled && onChange(!checked)}
      style={width ? { width: width } : {}}
    >
      {checked && <HBIcon type="ic_check" className={styles.checkIcon} />}
      {children}
    </div>
  );
}

function CheckGroup({
  options,
  value,
  onChange,
  isCheck,
  itemWidth,
  disabled,
}) {
  const [checkdList, setCheckList] = useControllableValue(
    { value: value },
    {
      defaultValue: [options[0]],
    }
  );

  const hasChecked = useCallback(
    (option) => {
      const checkedTargets = checkdList || [options[0]];
      return !!checkedTargets.find((item) => item.value == option.value);
    },
    [checkdList, options]
  );

  return (
    <div className={styles.checkGroup}>
      {options.map((opt) => {
        return (
          <CheckItem
            key={opt.value}
            width={itemWidth}
            checked={hasChecked(opt)}
            onChange={(checked) => {
              let next;
              if (checked) {
                if (isCheck) {
                  next = [...checkdList, opt];
                } else {
                  next = [opt];
                }
              } else {
                if (isCheck) {
                  next = without(checkdList, opt);
                } else {
                  return;
                }
              }
              onChange && onChange(next);
              setCheckList(next);
            }}
            disabled={disabled}
          >
            {opt.label}
          </CheckItem>
        );
      })}
    </div>
  );
}
